<template>
    <div
        class="modal"
        v-if="showCreditScoreModal"
        aria-labelledby="edit-program-modal"
        role="dialog"
        aria-modal="true"
    >
        <div class="modal-overlay" aria-hidden="true"></div>
        <div
            class="inner shadow"
            style="-webkit-transform: translate3d(0, 0, 0)"
        >
            <div
                class="items-start p-6 flex flex-col lg:!flex-row justify-center bg-white rounded-md"
            >
                <div class="ml-0">
                    <div class="flex justify-between items-center w-full">
                        <h3
                            class="text-xl text-slate-800 font-semibold mb-0"
                            v-if="mode === 'create'"
                        >
                            Add Credit Score
                        </h3>
                        <h3
                            class="text-xl md:text-2xl text-gray-800 font-semibold mb-0"
                            v-if="mode === 'edit'"
                        >
                            Edit Credit Score
                        </h3>
                        <a
                            href="#"
                            class="text-slate-600 hover:text-gray-400"
                            role="button"
                            @click.prevent="closeModal()"
                        >
                            <Icon icon="close" />
                        </a>
                    </div>
                    <p class="mb-8 text-sm text-slate-500 font-normal">
                        Check your credit score regularly in My10x for the best
                        recommendations
                    </p>

                    <div class="flex flex-col mb-2 gap-4">
                        <UiInput
                            id="creditscoredate"
                            name="score_date"
                            label="Date"
                            placeholder="Select Date ..."
                            :options="{
                                icon: 'calendar',
                            }"
                            :errors="errors"
                        >
                            <template #input>
                                <flat-pickr
                                    id="creditscoredate"
                                    :config="dateConfig"
                                    class="relative block w-full rounded border border-slate-200 bg-white py-1.5 pl-10 text-gray-900 placeholder:text-gray-400 focus:z-10 placeholder:text-sm focus:ring-0 active:ring-0 focus:border-slate-800 active:border-slate-800 mb-3"
                                    name="score_date"
                                    v-model="scoreData.scoreDate"
                                    placeholder="Select Date ..."
                                ></flat-pickr>
                            </template>
                        </UiInput>
                        <UiInput
                            id="score"
                            name="score"
                            label="FICO Score"
                            placeholder="Add Your Credit Score"
                            :options="{}"
                            :errors="errors"
                        >
                            <template #input>
                                <input
                                    type="number"
                                    name="score"
                                    v-model="scoreData.score"
                                    pattern="\d*"
                                    step="1"
                                    min="300"
                                    max="850"
                                    placeholder="Add Your Credit Score"
                                    @input="checkScoreLimits"
                                    class="relative block w-full rounded border border-slate-200 bg-white py-1.5 text-gray-900 placeholder:text-gray-400 focus:z-10 placeholder:text-sm focus:ring-0 active:ring-0 focus:border-slate-800 active:border-slate-800"
                                />
                                <span
                                    class="text-sm text-red-600"
                                    v-if="scoreMessage"
                                    v-html="scoreMessage"
                                ></span>
                            </template>
                        </UiInput>
                    </div>
                    <p class="mb-3">
                        <label class="mb-2 mr-3 flex flex-row">
                            <input
                                type="checkbox"
                                name="is_real_fico_score"
                                value="1"
                                class="form-checkbox mt-1"
                                v-model="scoreData.isRealFicoScore"
                            />
                            <span class="text-sm ml-2">
                                This is my real FICO score, not a Vantage 3.0 or
                                other score from sources like Credit Karma,
                                Credit Sesame, etc.
                                <a
                                    href="#"
                                    class="text-slate-800 text-sm font-medium"
                                    role="button"
                                    @click.prevent="
                                        showWhyMatter = !showWhyMatter
                                    "
                                >
                                    Why does this matter?
                                </a>
                            </span>
                        </label>
                    </p>

                    <div v-show="showWhyMatter" class="mb-5 relative">
                        <div
                            class="bg-gray-50 shadow px-6 py-5 sm:flex sm:items-start sm:justify-between"
                        >
                            <div class="sm:flex sm:items-start">
                                <div class="mt-3">
                                    <div class="text-sm text-gray-600 prose">
                                        <p>
                                            Many "FAKO" credit scores that are
                                            provided by sources such as
                                            CreditKarma, Credit Sesame, the free
                                            score from your bank, etc. are known
                                            to be inaccurate and therefore
                                            cannot be used to reliably track
                                            your credit score over time.
                                        </p>
                                        <p>
                                            Using an official source such as
                                            Experian Boost ensures that you are
                                            monitoring your "real" credit score
                                            and seeing the same information that
                                            most lenders are using to evaluate
                                            your credit.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div class="mt-4 sm:mt-0 sm:ml-6 sm:flex-shrink-0">
                                <a
                                    href="#"
                                    @click.prevent="showWhyMatter = false"
                                    role="button"
                                    class="absolute top-2 right-4 text-slate-600 hover:text-gray-400"
                                >
                                    <Icon icon="close" />
                                </a>
                            </div>
                        </div>
                    </div>

                    <p class="text-sm mt-3">
                        You can get your real FICO score for free using
                        <a
                            href="https://experianboost.com/"
                            target="_blank"
                            class="text-slate-800 text-sm font-medium"
                        >
                            Experian Boost
                        </a>
                        .
                    </p>
                    <Errors :errors="errors" />

                    <footer
                        class="flex !flex-row justify-between gap-2 items-center mt-8"
                    >
                        <button
                            type="button"
                            class="px-4 py-2.5 bg-gray-50 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-0"
                            @click.prevent="closeModal()"
                        >
                            Cancel
                        </button>
                        <button
                            v-if="mode === 'create'"
                            type="button"
                            class="px-4 py-2.5 bg-slate-700 rounded text-sm font-medium text-white hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-0 flex items-center justify-center"
                            @click.prevent="submitCreditScore"
                        >
                            <Icon icon="plus" class="text-orange-500 mr-2 w-5 h-5" pathClass="stroke-orange-500 stroke-[1.67]" />
                            Submit Credit Score
                            <span class="inline-flex ml-2" v-if="loadingResponse">
                                <i class="fas fa-spinner-third fa-spin"></i>
                            </span>
                        </button>
                        <button
                            v-if="mode === 'edit'"
                            type="button"
                            class="px-4 py-2.5 bg-slate-700 rounded text-sm font-medium text-white hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-0 flex items-center justify-center"
                            @click.prevent="updateScore"
                        >
                            <Icon icon="plus" class="text-orange-500 mr-2 w-5 h-5" pathClass="stroke-orange-500 stroke-[1.67]" />
                            Update Credit Score
                            <span class="inline-flex ml-2" v-if="loadingResponse">
                                <i class="fas fa-spinner-third fa-spin"></i>
                            </span>
                        </button>
                    </footer>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { useCreditScoreStore } from '@/store/creditscore';
import Errors from '@/components/errors.vue';
import FlatPickr from 'vue-flatpickr-component';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import Icon from '@/components/ui/icon/icon.vue';

export default {
    props: {},
    components: {
        FlatPickr,
        Errors,
        UiInput,
        Icon,
    },
    data: () => ({
        errors: [],
        scoreData: {
            scoreDate: new Date(),
            score: '',
            isRealFicoScore: false,
            uuid: null,
        },
        scoreMessage: null,
        dateConfig: {
            dateFormat: 'm/d/Y',
            maxDate: 'today',
        },
        mode: 'create',
        showWhyMatter: false,
        whyMatterHoverTimeout: null,
        popoverStyle: {
            position: 'fixed',
            top: '0px',
            left: '0px',
            zIndex: 9999,
        },
    }),
    directives: {},
    computed: {
        ...mapState(useCreditScoreStore, [
            'loadingResponse',
            'showCreditScoreModal',
            'activeScore',
            'users',
        ]),
    },
    watch: {
        showCreditScoreModal(newVal, oldVal) {
            if (newVal) {
                if (this.activeScore) {
                    this.scoreData = {
                        scoreDate: this.activeScore.score_date,
                        score: this.activeScore.score,
                        isRealFicoScore: this.activeScore.is_real_fico_score,
                        uuid: this.activeScore.uuid,
                        user_id: this.activeScore.user_id,
                    };
                    this.mode = 'edit';
                } else {
                    this.scoreData = {
                        scoreDate: new Date(),
                        score: '',
                        isRealFicoScore: false,
                    };
                    this.mode = 'create';
                }
            }
        },
    },
    methods: {
        ...mapActions(useCreditScoreStore, [
            'saveCreditScore',
            'updateCreditScore',
            'getUserScores',
            'setActiveScore',
            'setUsers',
            'setActiveUser',
        ]),

        clearTimeout(timeoutId) {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        },

        submitCreditScore() {
            this.validate();

            if (this.errors.length === 0) {
                this.saveCreditScore(this.scoreData)
                    .then(() => {
                        this.getUserScores().then(() => {
                            this.closeModal();
                        });
                    })
                    .catch((error) => {});
            }
        },
        updateScore() {
            this.validate();

            if (this.errors.length === 0) {
                this.updateCreditScore(this.scoreData)
                    .then(() => {
                        this.getUserScores().then(() => {
                            this.closeModal();
                        });
                    })
                    .catch((error) => {});
            }
        },
        checkScoreLimits() {
            const scoreMessage =
                'Please enter a score between<br />300 and 850';
            if (String(this.scoreData.score).length === 3) {
                if (
                    Math.floor(this.scoreData.score) >= 300 &&
                    Math.floor(this.scoreData.score) <= 850
                ) {
                    this.scoreMessage = null;
                }
                if (Math.floor(this.scoreData.score) < 300) {
                    this.scoreData.score = 300;
                    this.scoreMessage = scoreMessage;
                }
                if (Math.floor(this.scoreData.score) > 850) {
                    this.scoreData.score = 850;
                    this.scoreMessage = scoreMessage;
                }
            }
        },
        validate() {
            this.errors = [];
            this.scoreMessage = null;
            if (!this.scoreData.isRealFicoScore) {
                this.errors.push({
                    message: 'Please acknowledge that this is your FICO score.',
                });
            }
            if (Math.floor(this.scoreData.score) < 300) {
                this.errors.push({
                    message: 'The score must be at least 300.',
                });
            }
            if (Math.floor(this.scoreData.score) > 850) {
                this.errors.push({
                    message: 'The score cannot be more than 850.',
                });
            }
        },
        closeModal() {
            this.scoreData.scoreDate = '';
            this.scoreData.score = '';
            this.scoreData.isRealFicoScore = false;
            this.scoreData.uuid = null;
            this.errors = [];
            this.setActiveScore(null);
            this.mode = 'create';
            this.$emit('close');
        },
        toggleWhyMatter() {
            this.showWhyMatter = !this.showWhyMatter;
            if (this.showWhyMatter) {
                this.updatePopoverPosition();
                // Agregar un event listener para cerrar el popover al hacer clic en cualquier parte
                this.$nextTick(() => {
                    document.addEventListener(
                        'click',
                        this.closePopoverOnOutsideClick
                    );
                });
            } else {
                document.removeEventListener(
                    'click',
                    this.closePopoverOnOutsideClick
                );
            }
        },
        closePopoverOnOutsideClick(event) {
            // Si el popover está abierto y el clic no fue en el popover ni en el enlace
            const popover = document.querySelector('.fixed.z-50.flex.flex-col');
            const isClickInPopover = popover && popover.contains(event.target);
            const isClickInLink =
                this.$refs.whyMatterLink &&
                this.$refs.whyMatterLink.contains(event.target);

            if (this.showWhyMatter && !isClickInPopover && !isClickInLink) {
                this.showWhyMatter = false;
                document.removeEventListener(
                    'click',
                    this.closePopoverOnOutsideClick
                );
            }
        },
        showPopover() {
            this.showWhyMatter = true;
            this.updatePopoverPosition();
        },
        updatePopoverPosition() {
            this.$nextTick(() => {
                if (this.$refs.whyMatterLink) {
                    const linkRect =
                        this.$refs.whyMatterLink.getBoundingClientRect();

                    // Calcular la posición óptima para que sea visible
                    this.popoverStyle = {
                        top: `${linkRect.bottom + window.scrollY + 5}px`,
                        left: `${Math.max(10, linkRect.left + window.scrollX - 0)}px`, // Movido más a la derecha
                        zIndex: 9999,
                    };
                }
            });
        },
    },
    created() {},
    mounted() {
        window.addEventListener('resize', this.updatePopoverPosition);
        window.addEventListener('scroll', this.updatePopoverPosition);
        this.setUsers().then(() => {
            this.setActiveUser(this.users[0]);
        });
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.updatePopoverPosition);
        window.removeEventListener('scroll', this.updatePopoverPosition);
        document.removeEventListener('click', this.closePopoverOnOutsideClick);
    },
};
</script>
